import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Event } from '@nostr/tools/wasm'
import client from '@/services/client.service'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { tagNameEquals } from '@/lib/tag'
import Emoji from '../Emoji'
import { parseReactionEmoji } from '../ReactionList'
import MainNoteCard from './MainNoteCard'
import NoteOptions from '../NoteOptions'
import PendingUndoButton from '../PendingUndoButton'
import Username from '../Username'
import { FormattedTimestamp } from '../FormattedTimestamp'

export default function ReactionNoteCard({
  event,
  className,
  filterMutedNotes = true,
  pinned = false,
  groupedNotesTotalCount,
  onAllNotesRead,
  areAllNotesRead,
  displayScore,
  clamp = false
}: {
  event: Event
  className?: string
  filterMutedNotes?: boolean
  pinned?: boolean
  groupedNotesTotalCount?: number
  onAllNotesRead?: () => void
  areAllNotesRead?: boolean
  displayScore?: number
  clamp?: boolean
}) {
  const { t } = useTranslation()
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const [targetEvent, setTargetEvent] = useState<Event | null>(null)

  const shouldHide = useMemo(() => {
    if (!targetEvent) return true
    if (filterMutedNotes && mutePubkeySet.has(targetEvent.pubkey)) return true
    return false
  }, [targetEvent, filterMutedNotes, mutePubkeySet])

  useEffect(() => {
    const fetch = async () => {
      try {
        const eTag = event.tags.find(tagNameEquals('e'))
        if (eTag?.[1]) {
          const target = await client.fetchEvent({
            id: eTag[1],
            relays: eTag[2] ? [eTag[2]] : [],
            author: eTag[4]
          })
          if (target) {
            setTargetEvent(target)
            client.addEventToCache(target)
            return
          }
        }

        const aTag = event.tags.find(tagNameEquals('a'))
        if (aTag?.[1]) {
          const [kind, pubkey, ...identifier] = aTag[1].split(':')
          const target = await client.fetchEvent({
            kind: Number(kind),
            pubkey,
            identifier: identifier.join(':'),
            relays: aTag[2] ? [aTag[2]] : []
          })
          if (target) {
            setTargetEvent(target)
            client.addEventToCache(target)
          }
        }
      } catch {
        // ignore
      }
    }
    fetch()
  }, [event])

  if (!targetEvent || shouldHide) return null

  return (
    <>
      <PendingUndoButton event={event} />
      <div className={className}>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground px-4 pt-3 pb-1">
          <Emoji
            emoji={parseReactionEmoji(event)}
            classNames={{ text: 'text-lg', img: 'size-4' }}
          />
          <Username
            user={event.pubkey}
            className="font-semibold truncate"
            skeletonClassName="h-3"
          />
          <NoteOptions
            event={event}
            customTrigger={<span className="hover:underline cursor-pointer">{t('reacted')}</span>}
          />
          <FormattedTimestamp timestamp={event.created_at} />
        </div>
        <MainNoteCard
          event={targetEvent}
          pinned={pinned}
          groupedNotesTotalCount={groupedNotesTotalCount}
          onAllNotesRead={onAllNotesRead}
          areAllNotesRead={areAllNotesRead}
          displayScore={displayScore}
          clamp={clamp}
        />
      </div>
    </>
  )
}
